import { Controller } from "stimulus"
import { ChatSessionPreferenceService } from "../services/chat_session_preference_service"
import { EventTrackerService } from "../services/event_tracker_service"
export default class extends Controller {
  static targets = ["messageInput", "submitButton", "option", "messageForm", "onboardingModal"]
  static values = {
    initialMessage: String,
    scrollToBottom: Boolean,
    requestTimeout: Number,
    streamConnectTimeout: Number
  }

  connect() {
    this.resetForm()
    if (this.scrollToBottomValue) {
      this.scrollChatToBottom()
    }
    this.disableForm()
    this.availableClickableMessages = true
    this._streamConnectedHandler = this.handleInitialMessage.bind(this)
    document.addEventListener('stream-connected', this._streamConnectedHandler, { once: true })
    // Fallback: never leave the form frozen if the WebSocket never connects
    // (mobile web views, flaky networks). Re-enable after a short window.
    this._streamFallbackTimeoutId = setTimeout(() => this.enableForm(), this.streamConnectTimeoutValue || 3000)
    this.eventTracker = new EventTrackerService();
    this.eventTracker.initialize();
  }

  handleInitialMessage() {
    this.clearStreamFallback()
    const initialMessage = this.initialMessageValue

    this.enableForm()
    // Only auto-submit the initial message if the input is still untouched — the
    // WebSocket fallback may have enabled the form first and let the user start typing.
    if (initialMessage && !this.messageInputTarget.value.trim()) {
      this.messageInputTarget.value = initialMessage
      this.messageInputTarget.dispatchEvent(new Event('input'))
      this.messageFormTarget.requestSubmit()
      const url = new URL(window.location.href)
      url.searchParams.delete('initial_message')
      if (url.searchParams.has('weekly_highlight')) {
        url.searchParams.delete('weekly_highlight')
      }
      history.replaceState({}, '', url)
    }
  }

  disconnect() {
    if (this._streamConnectedHandler) {
      document.removeEventListener('stream-connected', this._streamConnectedHandler)
    }
    this.clearStreamFallback()
    this.stopAwaitingAssistant()
  }

  clearStreamFallback() {
    if (this._streamFallbackTimeoutId) {
      clearTimeout(this._streamFallbackTimeoutId)
      this._streamFallbackTimeoutId = null
    }
  }

  scrollChatToBottom() {
    setTimeout(() => {
      const messagesContainer = document.querySelector('.chat-messages-container')
      this.scrollToBottom(messagesContainer)
    }, 750)
  }

  handleKeyDown(event) {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault()
      this.messageFormTarget.requestSubmit()
    }
  }

  submitMessage(event) {
    event.preventDefault()
    if (this._awaitingAssistant) return

    const messageContent = this.messageInputTarget.value.trim()
    if (!messageContent) return

    this.sendMessageToServer(messageContent)
    this.scrollChatToBottom()
  }

  sendMessageToServer(messageContent) {
    if (this._awaitingAssistant) return

    this.disableForm()
    this._lastSentMessage = messageContent
    this.beginAwaitingAssistant()

    const form = this.messageFormTarget
    const formData = this.prepareFormData(form, messageContent)

    // Bound the enqueue request so a stalled POST cannot hang forever.
    // The form stays disabled until generation settles over ActionCable (or
    // the generation timeout fires) so rapid double-sends cannot skip serialization.
    const { signal, clear } = this.buildTimeoutSignal(this.requestTimeoutValue || 60000)
    fetch(form.action, {
      method: 'POST',
      body: formData,
      headers: { "Accept": "application/json" },
      signal
    })
      .then(response => this.handleResponse(response))
      .then(data => this.handleSuccess(data))
      .catch(error => this.handleError(error))
      .finally(() => {
        clear()
      })
  }

  // Prefer the native AbortSignal.timeout; fall back to AbortController + setTimeout
  // for older mobile web views (iOS < 16, legacy Android WebView) that lack it.
  // Returns { signal, clear }; clear() cancels the fallback timer once the request settles.
  buildTimeoutSignal(ms) {
    if (typeof AbortSignal !== "undefined" && typeof AbortSignal.timeout === "function") {
      return { signal: AbortSignal.timeout(ms), clear: () => {} }
    }
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), ms)
    return { signal: controller.signal, clear: () => clearTimeout(timeoutId) }
  }

  prepareFormData(form, messageContent) {
    const formData = new FormData()
    formData.append('content', messageContent)

    const chatSessionInput = form.querySelector('input[name="chat_session_id"]')
    if (chatSessionInput) {
      formData.append('chat_session_id', chatSessionInput.value)
    }

    const token = document.querySelector('meta[name="csrf-token"]').content
    formData.append('authenticity_token', token)

    return formData
  }

  handleResponse(response) {
    if (!response.ok) {
      throw new Error(`Network response was not ok: ${response.status}`)
    }
    return response.json()
  }

  handleSuccess(data) {
    this.clearInput()
    this.enableNewChatLink()

    if (data.chat_session?.is_first_message) {
      this.addConversationToSidebar(data.chat_session)
      this.updateHeaderTitle(data.chat_session.title)
    } else {
      this.updateSidebarSessionTitle()
    }
  }

  handleError(error) {
    this.stopAwaitingAssistant()
    this.enableForm()

    // Timeout/abort is the intentional backstop firing — expected, not a failure.
    if (error?.name === 'TimeoutError' || error?.name === 'AbortError') {
      console.warn('Chat request timed out; input re-enabled.')
      return
    }
    console.error('Error sending message:', error)
  }

  scrollToBottom(container) {
    if (container) {
      container.scrollTop = container.scrollHeight
    }
  }

  resetForm() {
    this.clearInput()
    this.enableForm()
    this.messageInputTarget.focus()
  }

  clearInput() {
    this.messageInputTarget.value = ''
    this.messageInputTarget.dispatchEvent(new Event('input'))
  }

  disableForm() {
    this.messageInputTarget.disabled = true
    this.submitButtonTarget.disabled = true
    this.submitButtonTarget.classList.add('tw-opacity-50')
  }

  enableForm() {
    this.messageInputTarget.disabled = false
    this.submitButtonTarget.disabled = false
    this.submitButtonTarget.classList.remove('tw-opacity-50')
  }

  beginAwaitingAssistant() {
    this._awaitingAssistant = true
    this.observeAssistantArrival()
    this.clearGenerationTimeout()
    this._generationTimeoutId = setTimeout(() => {
      this.stopAwaitingAssistant()
      this.enableForm()
    }, this.requestTimeoutValue || 60000)
  }

  observeAssistantArrival() {
    const marker = document.getElementById('chat-generation-settled')
    if (!marker) return

    this._assistantObserver?.disconnect()
    this._assistantObserver = new MutationObserver(() => {
      this.stopAwaitingAssistant()
      this.enableForm()
      this.scrollChatToBottom()
    })
    this._assistantObserver.observe(marker, { childList: true, characterData: true, subtree: true })
  }

  stopAwaitingAssistant() {
    this._awaitingAssistant = false
    this.availableClickableMessages = true
    this._assistantObserver?.disconnect()
    this._assistantObserver = null
    this.clearGenerationTimeout()
  }

  clearGenerationTimeout() {
    if (this._generationTimeoutId) {
      clearTimeout(this._generationTimeoutId)
      this._generationTimeoutId = null
    }
  }

  selectOption(event) {
    if (!this.availableClickableMessages) return
    this.availableClickableMessages = false

    event.preventDefault()
    const messageContent = event.currentTarget.dataset.message
    if (!messageContent) return

    this.messageInputTarget.value = messageContent
    this.messageInputTarget.dispatchEvent(new Event('input'))
    this.sendMessageToServer(messageContent)
    this.disableAllOptions()
    this.scrollChatToBottom()
  }

  disableAllOptions() {
    this.optionTargets.forEach(button => {
      button.disabled = true
    })
  }

  enableNewChatLink() {
    document.querySelectorAll('.new-chat-disabled').forEach(link => {
      link.classList.remove('new-chat-disabled', 'tw-text-gray-400')
      link.classList.add('tw-text-blue-lotus')
      link.removeAttribute('tabindex')
    })
  }

  updateSidebarSessionTitle() {
    const messageContent = this._lastSentMessage
    if (!messageContent) return

    const chatSessionInput = this.messageFormTarget.querySelector('input[name="chat_session_id"]')
    if (!chatSessionInput) return

    const titleElement = document.getElementById(`sidebar-session-title-${chatSessionInput.value}`)
    if (titleElement && titleElement.textContent.trim() === 'New conversation') {
      const maxLength = 50
      const truncated = messageContent.length > maxLength
        ? messageContent.substring(0, maxLength - 3) + '...'
        : messageContent
      titleElement.textContent = truncated
    }

    this._lastSentMessage = null
  }

  addConversationToSidebar(chatSession) {
    const sidebar = document.querySelector('[data-chat-sidebar-target="historyList"]')
    if (!sidebar) return

    const emptyState = sidebar.querySelector('.tw-text-center')
    if (emptyState) {
      sidebar.innerHTML = chatSession.today_section_html

      const deleteAllContainer = document.createElement('div')
      deleteAllContainer.className = 'tw-flex tw-justify-end tw-mb-3'
      deleteAllContainer.innerHTML = '<a class="chat-history-delete-all-link" href="#" data-action="click->chat-history#showDeleteAllModal">Delete all</a>'
      sidebar.insertBefore(deleteAllContainer, sidebar.firstChild)
    } else {
      this.addToExistingConversationList(sidebar, chatSession)
    }
  }

  addToExistingConversationList(sidebar, chatSession) {
    sidebar.querySelectorAll('.sidebar-conversation-active').forEach(el => {
      el.classList.remove('sidebar-conversation-active')
      el.classList.add('tw-text-black')
    })

    const todaySection = Array.from(sidebar.querySelectorAll('h4')).find(h4 => h4.textContent.trim() === 'Today')

    if (todaySection) {
      todaySection.insertAdjacentHTML('afterend', chatSession.sidebar_html)
    } else {
      sidebar.insertAdjacentHTML('afterbegin', chatSession.today_section_html)
    }
  }

  updateHeaderTitle(title) {
    if (!title) return

    const headerTitle = document.querySelector('.chat-header .font-greycliff')
    if (headerTitle) {
      headerTitle.textContent = title
    }
  }

  showOnboardingModal() {
    this.onboardingModalTarget.style.display = ''
    const modalElement = this.onboardingModalTarget.querySelector('[data-modal-target="modal"]')
    modalElement.style.display = 'flex'

    const modalControllerElement = this.onboardingModalTarget.querySelector('[data-controller*="modal"]')
    if (modalControllerElement) {
      const openEventName = modalControllerElement.dataset.modalOpenEventNameValue
      const openEventProperties = modalControllerElement.dataset.modalOpenEventPropertiesValue
  
      if (openEventName) {
        this.eventTracker.trackEvent(openEventName, openEventProperties, modalControllerElement)
      }
    }
  }

}